/**
 * Single WebSocket-per-app-session manager that streams ScrapeJob lifecycle
 * events into the Redux scrapeJobs slice. Mounted exactly once at app
 * shell level (sibling of <Header />) as <ScrapeJobsSocketManager />.
 *
 * - Connects to ws://.../ws/scrape-jobs/<office_id>/?token=<auth>
 * - Reconnects with exponential backoff (1s -> 30s capped)
 * - Falls back to 5s polling of GET /clinics/api/scrape-jobs/ when WS is
 *   disconnected for >30s
 * - On every (re)connect, dispatches a BULK_SYNC from the REST list
 *
 * Implemented as a connect()-wrapped component (not a hook) because this
 * codebase is on react-redux 6.x — pre-hooks API.
 */

import React from "react";
import { connect } from "react-redux";

import config from "../config";
import { apiService } from "../_services/api.service";
import { debugLog } from "../utils/debugLog";
import {
  SCRAPE_JOB_UPSERT,
  SCRAPE_JOBS_BULK_SYNC,
  SCRAPE_JOBS_WS_CONNECTED,
  SCRAPE_JOBS_WS_DISCONNECTED,
  SCRAPE_JOBS_SET_OFFICE,
} from "../reducers/scrapeJobs";

const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const POLLING_FALLBACK_MS = 5000;
const POLLING_FALLBACK_AFTER_MS = 30000;
const ACTIVE_STATES = "queued,claimed,running,awaiting_2fa";
// Application-level ping. Some intermediates (proxies, Daphne idle timers,
// OS network stacks) close idle WebSockets after ~30-60s without traffic,
// emitting only a code-1006 abnormal-close that arrives much later. Pinging
// at 25s keeps the path warm; the 60s watchdog detects the dead-socket
// case where the ping write succeeded but no pong came back.
const PING_INTERVAL_MS = 25000;
const WATCHDOG_TIMEOUT_MS = 60000;

function deriveWsUrl(apiUrl) {
  if (!apiUrl) return "";
  if (apiUrl.startsWith("https://")) return "wss://" + apiUrl.slice("https://".length);
  if (apiUrl.startsWith("http://")) return "ws://" + apiUrl.slice("http://".length);
  return apiUrl;
}

function readAuthTokenFromStorage() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return (parsed && (parsed.token || parsed.key)) || "";
  } catch (e) {
    return "";
  }
}

function readOfficeIdFromAuth(auth) {
  // The codebase stores the active office in localStorage (set by TopBar).
  // Prefer that since Redux auth state doesn't always carry the office id.
  try {
    const sel = localStorage.getItem("selectedOffice");
    if (sel) {
      const n = parseInt(sel, 10);
      if (!isNaN(n)) return n;
    }
  } catch (e) {
    /* fall through */
  }
  try {
    const list = JSON.parse(localStorage.getItem("office_ids") || "null");
    if (list && list.length && list[0] && list[0].office_id) {
      const n = parseInt(list[0].office_id, 10);
      if (!isNaN(n)) return n;
    }
  } catch (e) {
    /* fall through */
  }
  if (!auth || !auth.user) return null;
  const u = auth.user;
  if (u.office_id) return u.office_id;
  if (u.office && u.office.id) return u.office.id;
  if (u.user_office && u.user_office.id) return u.user_office.id;
  if (u.offices && u.offices.length) return u.offices[0].id || u.offices[0];
  return null;
}

class ScrapeJobsSocketManager extends React.Component {
  constructor(props) {
    super(props);
    this.ws = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.pollingTimer = null;
    this.pingTimer = null;
    this.watchdogTimer = null;
    this.pingsSent = 0;
    this.pongsRecv = 0;
    this.lastSeenAt = 0;
    this.lastConnectedAt = null;
    this.cancelled = false;
  }

  componentDidMount() {
    this.cancelled = false;
    if (this.props.officeId) {
      this.props.setOffice(this.props.officeId);
      this.open();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.officeId !== this.props.officeId) {
      this.teardown();
      // Re-scope the store to the newly-selected office (drops the previous
      // office's jobs so surfaces never show cross-office runs).
      this.props.setOffice(this.props.officeId || null);
      this.cancelled = false;
      if (this.props.officeId) {
        this.open();
      }
    }
  }

  componentWillUnmount() {
    this.teardown();
  }

  teardown() {
    this.cancelled = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopPollingFallback();
    this.stopHeartbeat();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      try { ws.close(); } catch (e) { /* noop */ }
    }
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.pingsSent = 0;
    this.pongsRecv = 0;
    this.lastSeenAt = Date.now();
    this.pingTimer = setInterval(this.sendPing, PING_INTERVAL_MS);
    this.bumpWatchdog();
  }

  stopHeartbeat() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  sendPing = () => {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "ping" }));
      this.pingsSent += 1;
    } catch (e) {
      // Send failed — socket is effectively dead. Force close to trigger
      // the existing reconnect path; don't wait for the OS-level close.
      try { ws.close(); } catch (e2) { /* noop */ }
    }
  };

  // Reset on every inbound frame (data event or pong). If 60s elapses with
  // nothing coming back, treat the socket as dead and reconnect.
  bumpWatchdog() {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.watchdogTimer = setTimeout(this.onWatchdogFired, WATCHDOG_TIMEOUT_MS);
  }

  onWatchdogFired = () => {
    const sinceLastSeen = this.lastSeenAt ? Date.now() - this.lastSeenAt : -1;
    debugLog.warn("ws", "watchdog_fired", {
      delay: WATCHDOG_TIMEOUT_MS,
      // Diagnostic: how many pings we sent vs. pongs/messages we got back.
      // Mismatch tells us whether the FE→BE path or BE→FE path failed.
      attempt: this.pingsSent,
      count: this.pongsRecv,
      // Time since the last inbound frame of any kind (pong or event).
      // If this is much smaller than `delay`, the watchdog timer drifted.
      interval: sinceLastSeen,
    });
    const ws = this.ws;
    if (ws) {
      try { ws.close(); } catch (e) { /* noop */ }
    }
    // onclose handler will run next and schedule reconnect.
  };

  bulkSync = async () => {
    try {
      const params = new URLSearchParams();
      if (this.props.lastSyncAt) {
        // Incremental sync: reconcile EVERYTHING updated since the last sync —
        // including jobs that reached a terminal state while the socket was
        // momentarily disconnected. Filtering to active-only here would miss
        // those completions, leaving a run stuck "running" in the store until
        // a full refetch.
        params.set("since", this.props.lastSyncAt);
        params.set("limit", "200");
      } else {
        // First sync of the session: just the currently-active jobs, so we
        // don't pull the entire history on connect.
        params.set("state__in", ACTIVE_STATES);
      }
      const data = (await apiService.get("/clinics/api/scrape-jobs/?" + params.toString())) || {};
      this.props.bulkSync(data.results || [], data.now || new Date().toISOString());
      debugLog.info("ws", "bulk_sync_complete", { count: (data.results || []).length });
    } catch (e) {
      // soft-fail; polling continues, ws reconnects on its own
      debugLog.warn("ws", "bulk_sync_failed", { reason: (e && e.message) || "unknown" });
    }
  };

  startPollingFallback() {
    if (this.pollingTimer) return;
    debugLog.warn("ws", "polling_fallback_started", { interval: POLLING_FALLBACK_MS });
    this.pollingTimer = setInterval(this.bulkSync, POLLING_FALLBACK_MS);
  }

  stopPollingFallback() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
      debugLog.info("ws", "polling_fallback_stopped");
    }
  }

  scheduleReconnect = () => {
    if (this.cancelled) return;
    const attempt = this.reconnectAttempts;
    const delay = Math.min(RECONNECT_MIN_MS * Math.pow(2, attempt), RECONNECT_MAX_MS);
    this.reconnectAttempts = attempt + 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(this.open, delay);
    debugLog.info("ws", "reconnect_scheduled", { attempt, delay });

    const sinceDisconnect = this.lastConnectedAt
      ? Date.now() - this.lastConnectedAt
      : Infinity;
    if (sinceDisconnect > POLLING_FALLBACK_AFTER_MS) {
      this.startPollingFallback();
    }
  };

  open = () => {
    if (this.cancelled) return;
    const officeId = this.props.officeId;
    if (!officeId) return;
    const token = readAuthTokenFromStorage();
    if (!token) return;

    const base = deriveWsUrl(config.apiGateway && config.apiGateway.URL);
    if (!base) return;
    const url =
      base + "/ws/scrape-jobs/" + officeId + "/?token=" + encodeURIComponent(token);

    debugLog.info("ws", "ws_open_attempt", { office_id: officeId });

    try {
      const ws = new WebSocket(url);
      this.ws = ws;

      ws.onopen = () => {
        if (this.cancelled) {
          try { ws.close(); } catch (e) { /* noop */ }
          return;
        }
        this.reconnectAttempts = 0;
        this.lastConnectedAt = Date.now();
        this.stopPollingFallback();
        this.startHeartbeat();
        this.props.markConnected();
        debugLog.info("ws", "ws_opened", { office_id: officeId });
        this.bulkSync();
      };

      ws.onmessage = (ev) => {
        // Any inbound frame proves the path is live — reset the watchdog
        // before we even parse, so a malformed message still keeps the
        // socket considered healthy.
        this.bumpWatchdog();
        this.lastSeenAt = Date.now();
        let data;
        try {
          data = JSON.parse(ev.data);
        } catch (e) {
          return;
        }
        if (!data || !data.type) return;
        if (data.type === "connected") return;
        if (data.type === "pong") {
          this.pongsRecv += 1;
          return;
        }
        if (data.job_id) {
          debugLog.info("ws", "ws_message", {
            job_id: data.job_id,
            event_type: data.type,
            state: data.state,
          });
          this.props.upsertJob(data);
        }
      };

      ws.onerror = () => {
        // onclose runs next; reconnection handled there.
        debugLog.warn("ws", "ws_error");
      };

      ws.onclose = (ev) => {
        this.ws = null;
        this.stopHeartbeat();
        this.props.markDisconnected();
        debugLog.warn("ws", "ws_closed", { http_status: ev && ev.code });
        this.scheduleReconnect();
      };
    } catch (e) {
      debugLog.error("ws", "ws_open_threw", { reason: (e && e.message) || "unknown" });
      this.scheduleReconnect();
    }
  };

  render() {
    return null;
  }
}

const mapStateToProps = (state) => ({
  officeId: readOfficeIdFromAuth(state.authentication),
  lastSyncAt: state.scrapeJobs ? state.scrapeJobs.lastSyncAt : null,
});

const mapDispatchToProps = (dispatch) => ({
  upsertJob: (job) => dispatch({ type: SCRAPE_JOB_UPSERT, job }),
  bulkSync: (jobs, lastSyncAt) =>
    dispatch({ type: SCRAPE_JOBS_BULK_SYNC, jobs, lastSyncAt }),
  markConnected: () => dispatch({ type: SCRAPE_JOBS_WS_CONNECTED }),
  markDisconnected: () => dispatch({ type: SCRAPE_JOBS_WS_DISCONNECTED }),
  setOffice: (officeId) => dispatch({ type: SCRAPE_JOBS_SET_OFFICE, officeId }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ScrapeJobsSocketManager);
